/**
 * B2BSettingsPage — Personal notification preferences for B2B clients
 *
 * Route: /b2b/settings  (nested under B2BLayout)
 *
 * Shows:
 *  - Notification channel toggles per B2B event type (all off by default)
 *  - CC emails management (up to 2 additional addresses)
 *
 * Data is keyed by inventory_clients.id, fully independent of the main system.
 */

import { useEffect, useState, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  ArrowRight,
  Bell,
  Mail,
  MessageSquare,
  Smartphone,
  Plus,
  X,
  Loader as Loader2,
  CircleCheck as CheckCircle2,
  CircleAlert as AlertCircle,
  AtSign,
  Settings,
  Pencil,
} from 'lucide-react';
import { supabase } from '@/lib/supabase';
import { useB2BUser } from './B2BUserContext';

// ─── Types ──────────────────────────────────────────────────────────────────

interface B2BEventPreference {
  event_key: string;
  label_he: string;
  description_he: string;
  sort_order: number;
  whatsapp: boolean;
  email: boolean;
  sms: boolean;
}

type Channel = 'whatsapp' | 'email' | 'sms';

const CHANNEL_META: Array<{
  key: Channel;
  label: string;
  icon: React.ReactNode;
  color: string;
  activeColor: string;
}> = [
  { key: 'whatsapp', label: 'WhatsApp', icon: <MessageSquare className="w-4 h-4" />, color: 'text-emerald-600', activeColor: 'bg-emerald-600' },
  { key: 'email', label: 'מייל', icon: <Mail className="w-4 h-4" />, color: 'text-blue-600', activeColor: 'bg-blue-600' },
  { key: 'sms', label: 'SMS', icon: <Smartphone className="w-4 h-4" />, color: 'text-amber-600', activeColor: 'bg-amber-600' },
];

// ─── Toggle component ───────────────────────────────────────────────────────

function Toggle({ checked, onChange, disabled }: {
  checked: boolean;
  onChange: (v: boolean) => void;
  disabled?: boolean;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      disabled={disabled}
      onClick={() => onChange(!checked)}
      className={`relative inline-flex h-5 w-9 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent
        transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-1
        ${checked ? 'bg-primary-600' : 'bg-gray-200'}
        ${disabled ? 'opacity-40 cursor-not-allowed' : ''}`}
    >
      <span
        className={`pointer-events-none inline-block h-4 w-4 rounded-full bg-white shadow transform
          transition-transform duration-200 ease-in-out
          ${checked ? 'translate-x-4' : 'translate-x-0'}`}
      />
    </button>
  );
}

// ─── Main page ──────────────────────────────────────────────────────────────

export function B2BSettingsPage() {
  const navigate = useNavigate();
  const { session } = useB2BUser();

  const clientId = session?.activeClientId ?? null;
  const clientName = session?.activeClientRecord?.name ?? '';

  // ── Preferences state ─────────────────────────────────────────────────────
  const [preferences, setPreferences] = useState<B2BEventPreference[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [saving, setSaving] = useState<Set<string>>(new Set());

  // ── Primary email state ────────────────────────────────────────────────────
  const [primaryEmail, setPrimaryEmail] = useState<string>('');
  const [primaryEmailInput, setPrimaryEmailInput] = useState('');
  const [primaryEmailEditing, setPrimaryEmailEditing] = useState(false);
  const [primaryEmailSaving, setPrimaryEmailSaving] = useState(false);
  const [primaryEmailError, setPrimaryEmailError] = useState<string | null>(null);

  // ── CC emails state ───────────────────────────────────────────────────────
  const [ccEmails, setCcEmails] = useState<string[]>([]);
  const [ccInput, setCcInput] = useState('');
  const [ccError, setCcError] = useState<string | null>(null);
  const [ccSaving, setCcSaving] = useState(false);

  // ── Toast ─────────────────────────────────────────────────────────────────
  const [toast, setToast] = useState<{ kind: 'success' | 'error'; message: string } | null>(null);
  const showToast = useCallback((kind: 'success' | 'error', message: string) => {
    setToast({ kind, message });
    setTimeout(() => setToast(null), 3500);
  }, []);

  // ── Load preferences + CC emails ─────────────────────────────────────────
  useEffect(() => {
    if (!clientId) return;
    let cancelled = false;
    setLoading(true);
    setError(null);

    Promise.all([
      supabase.rpc('get_b2b_notification_preferences', { p_client_id: clientId }),
      supabase
        .from('inventory_clients')
        .select('email, notification_cc_emails')
        .eq('id', clientId)
        .maybeSingle(),
    ])
      .then(([prefsResult, ccResult]) => {
        if (cancelled) return;
        if (prefsResult.error) {
          setError('שגיאה בטעינת העדפות ההתראות');
          console.error('[B2BSettings] prefs RPC error:', prefsResult.error);
          setLoading(false);
          return;
        }
        setPreferences((prefsResult.data ?? []) as B2BEventPreference[]);

        if (ccResult.data) {
          const clientEmail = (ccResult.data.email as string) ?? '';
          setPrimaryEmail(clientEmail);
          setPrimaryEmailInput(clientEmail);
          if (ccResult.data.notification_cc_emails) {
            setCcEmails(ccResult.data.notification_cc_emails as string[]);
          }
        }
        setLoading(false);
      })
      .catch(err => {
        if (cancelled) return;
        console.error('[B2BSettings] load error:', err);
        setError('שגיאה בטעינת ההגדרות');
        setLoading(false);
      });

    return () => { cancelled = true; };
  }, [clientId]);

  // ── Primary email save ────────────────────────────────────────────────────
  const handleSavePrimaryEmail = useCallback(async () => {
    if (!clientId) return;
    const email = primaryEmailInput.trim().toLowerCase();
    setPrimaryEmailError(null);

    if (!email) { setPrimaryEmailError('נא להזין כתובת אימייל'); return; }
    if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)) { setPrimaryEmailError('כתובת אימייל לא תקינה'); return; }

    setPrimaryEmailSaving(true);
    const { error: rpcError } = await supabase.rpc('update_b2b_client_email', {
      p_client_id: clientId,
      p_email: email,
    });
    setPrimaryEmailSaving(false);

    if (rpcError) {
      setPrimaryEmailError('שגיאה בשמירת כתובת המייל');
      console.error('[B2BSettings] primary email save error:', rpcError);
      return;
    }

    setPrimaryEmail(email);
    setPrimaryEmailEditing(false);
    showToast('success', 'כתובת מייל ראשית נשמרה בהצלחה');
  }, [clientId, primaryEmailInput, showToast]);

  // ── Toggle a channel ──────────────────────────────────────────────────────
  const handleToggle = useCallback(async (eventKey: string, channel: Channel, value: boolean) => {
    if (!clientId) return;

    // Guard: cannot enable email without a primary email
    if (channel === 'email' && value && !primaryEmail) {
      showToast('error', 'יש להגדיר כתובת מייל ראשית לפני הפעלת הודעות מייל');
      return;
    }

    const savingKey = `${eventKey}:${channel}`;
    setSaving(prev => new Set(prev).add(savingKey));

    // Optimistic update
    setPreferences(prev =>
      prev.map(p =>
        p.event_key === eventKey ? { ...p, [channel]: value } : p,
      ),
    );

    const pref = preferences.find(p => p.event_key === eventKey);
    const params = {
      p_client_id: clientId,
      p_event_key: eventKey,
      p_whatsapp: channel === 'whatsapp' ? value : (pref?.whatsapp ?? false),
      p_email: channel === 'email' ? value : (pref?.email ?? false),
      p_sms: channel === 'sms' ? value : (pref?.sms ?? false),
    };

    const { error: rpcError } = await supabase.rpc('upsert_b2b_notification_preference', params);

    setSaving(prev => {
      const next = new Set(prev);
      next.delete(savingKey);
      return next;
    });

    if (rpcError) {
      // Revert optimistic update
      setPreferences(prev =>
        prev.map(p =>
          p.event_key === eventKey ? { ...p, [channel]: !value } : p,
        ),
      );
      showToast('error', 'שגיאה בשמירת ההעדפה');
      console.error('[B2BSettings] upsert error:', rpcError);
    }
  }, [clientId, preferences, showToast]);

  // ── CC email management ───────────────────────────────────────────────────
  const isValidEmail = (e: string) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(e.trim());

  const handleAddCcEmail = useCallback(async () => {
    if (!clientId) return;
    const email = ccInput.trim().toLowerCase();
    setCcError(null);

    if (!email) { setCcError('נא להזין כתובת אימייל'); return; }
    if (!isValidEmail(email)) { setCcError('כתובת אימייל לא תקינה'); return; }
    if (ccEmails.includes(email)) { setCcError('כתובת זו כבר קיימת'); return; }
    if (ccEmails.length >= 2) { setCcError('ניתן להוסיף עד 2 כתובות נוספות'); return; }

    const newList = [...ccEmails, email];
    setCcSaving(true);

    const { error: rpcError } = await supabase.rpc('update_b2b_client_cc_emails', {
      p_client_id: clientId,
      p_cc_emails: newList,
    });

    setCcSaving(false);
    if (rpcError) {
      showToast('error', 'שגיאה בשמירת כתובת המייל');
      console.error('[B2BSettings] cc email add error:', rpcError);
      return;
    }

    setCcEmails(newList);
    setCcInput('');
    showToast('success', 'כתובת מייל נוספה בהצלחה');
  }, [clientId, ccInput, ccEmails, showToast]);

  const handleRemoveCcEmail = useCallback(async (email: string) => {
    if (!clientId) return;
    const newList = ccEmails.filter(e => e !== email);
    setCcSaving(true);

    const { error: rpcError } = await supabase.rpc('update_b2b_client_cc_emails', {
      p_client_id: clientId,
      p_cc_emails: newList,
    });

    setCcSaving(false);
    if (rpcError) {
      showToast('error', 'שגיאה בהסרת כתובת המייל');
      console.error('[B2BSettings] cc email remove error:', rpcError);
      return;
    }

    setCcEmails(newList);
    showToast('success', 'כתובת מייל הוסרה');
  }, [clientId, ccEmails, showToast]);

  // ── Render ────────────────────────────────────────────────────────────────

  if (!clientId) {
    return (
      <div className="max-w-lg mx-auto px-4 py-10 text-center text-gray-500">
        <Settings className="w-10 h-10 mx-auto text-gray-300 mb-3" />
        <p className="text-sm">יש לבחור לקוח פעיל כדי לגשת להגדרות</p>
      </div>
    );
  }

  return (
    <div className="max-w-lg mx-auto px-4 py-6 space-y-6">

      {/* ── Toast ──────────────────────────────────────────────────────────── */}
      {toast && (
        <div className={`fixed top-4 right-4 left-4 md:left-auto md:w-80 z-[60]
                        flex items-start gap-3 px-4 py-3 rounded-2xl shadow-xl text-sm font-medium
                        ${toast.kind === 'success' ? 'bg-green-600 text-white' : 'bg-red-600 text-white'}`}>
          {toast.kind === 'success'
            ? <CheckCircle2 className="w-4 h-4 shrink-0 mt-0.5" />
            : <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />}
          <span className="flex-1">{toast.message}</span>
          <button onClick={() => setToast(null)} className="p-0.5 opacity-70 hover:opacity-100">
            <X className="w-3.5 h-3.5" />
          </button>
        </div>
      )}

      {/* ── Header ─────────────────────────────────────────────────────────── */}
      <div className="flex items-center gap-3">
        <button
          onClick={() => navigate('/b2b/dashboard')}
          className="p-2 rounded-xl hover:bg-gray-100 transition-colors text-gray-500"
          aria-label="חזרה"
        >
          <ArrowRight className="w-5 h-5" />
        </button>
        <div className="flex-1 min-w-0">
          <h1 className="text-lg font-bold text-gray-900">הגדרות אישיות</h1>
          {clientName && (
            <p className="text-xs text-gray-500 truncate">{clientName}</p>
          )}
        </div>
        <div className="p-2 rounded-xl bg-primary-50">
          <Settings className="w-5 h-5 text-primary-600" />
        </div>
      </div>

      {/* ── Loading / Error ────────────────────────────────────────────────── */}
      {loading && (
        <div className="flex items-center justify-center py-12">
          <Loader2 className="w-6 h-6 animate-spin text-primary-500" />
        </div>
      )}

      {error && !loading && (
        <div className="flex items-start gap-2.5 px-4 py-3 bg-red-50 border border-red-200 rounded-xl text-sm text-red-700">
          <AlertCircle className="w-4 h-4 shrink-0 mt-0.5" />
          <span>{error}</span>
        </div>
      )}

      {!loading && !error && (
        <>
          {/* ── Notification Preferences ─────────────────────────────────────── */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <Bell className="w-4 h-4 text-primary-600" />
              <h2 className="text-sm font-bold text-gray-900">העדפות הודעות</h2>
            </div>

            <p className="text-xs text-gray-500 px-1">
              בחר באילו ערוצים תרצה לקבל הודעות לגבי כל אירוע.
              כברירת מחדל, לא נשלחות הודעות — הפעל את הערוצים הרצויים.
            </p>

            {/* Channel legend */}
            <div className="flex items-center gap-4 px-1">
              {CHANNEL_META.map(ch => (
                <span key={ch.key} className={`flex items-center gap-1 text-[11px] font-medium ${ch.color}`}>
                  {ch.icon}
                  {ch.label}
                </span>
              ))}
            </div>

            {/* Event rows */}
            <div className="space-y-2">
              {preferences.map(pref => (
                <div
                  key={pref.event_key}
                  className="bg-white rounded-2xl border border-gray-100 p-4 shadow-sm space-y-3"
                >
                  <div>
                    <p className="text-sm font-semibold text-gray-900">{pref.label_he}</p>
                    <p className="text-xs text-gray-500 mt-0.5">{pref.description_he}</p>
                  </div>

                  <div className="flex items-center gap-5">
                    {CHANNEL_META.map(ch => {
                      const isOn = pref[ch.key];
                      const isSaving = saving.has(`${pref.event_key}:${ch.key}`);
                      const emailDisabled = ch.key === 'email' && !primaryEmail;
                      return (
                        <div key={ch.key} className="flex items-center gap-2 relative group">
                          <span className={`text-[11px] font-medium ${emailDisabled ? 'text-gray-300' : ch.color}`}>
                            {ch.icon}
                          </span>
                          <Toggle
                            checked={isOn}
                            onChange={v => handleToggle(pref.event_key, ch.key, v)}
                            disabled={isSaving || emailDisabled}
                          />
                          {emailDisabled && (
                            <span className="absolute -top-8 left-1/2 -translate-x-1/2 bg-gray-800 text-white text-[10px] px-2 py-1 rounded opacity-0 group-hover:opacity-100 transition-opacity whitespace-nowrap pointer-events-none z-10">
                              יש להגדיר מייל ראשית
                            </span>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              ))}
            </div>
          </section>

          {/* ── CC Emails ────────────────────────────────────────────────────── */}
          {/* ── Primary email section ── */}
          <section className="space-y-3">
            <div className="flex items-center gap-2 px-1">
              <Mail className="w-4 h-4 text-blue-600" />
              <h2 className="text-sm font-bold text-gray-900">כתובת מייל ראשית</h2>
            </div>
            <p className="text-xs text-gray-500 px-1">
              יש להגדיר כתובת מייל ראשית כדי לקבל הודעות במייל.
            </p>

            <div className="bg-white border border-gray-200 rounded-xl p-4">
              {primaryEmail && !primaryEmailEditing ? (
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <CheckCircle2 className="w-4 h-4 text-green-600" />
                    <span className="text-sm text-gray-700" dir="ltr">{primaryEmail}</span>
                  </div>
                  <button
                    onClick={() => { setPrimaryEmailEditing(true); setPrimaryEmailInput(primaryEmail); setPrimaryEmailError(null); }}
                    className="flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition-colors"
                  >
                    <Pencil className="w-3.5 h-3.5" />
                    <span>שינוי</span>
                  </button>
                </div>
              ) : (
                <div className="space-y-2">
                  <div className="flex gap-2">
                    <input
                      type="email"
                      dir="ltr"
                      className="flex-1 text-sm border border-gray-300 rounded-lg px-3 py-2 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                      placeholder="email@example.com"
                      value={primaryEmailInput}
                      onChange={(e) => { setPrimaryEmailInput(e.target.value); setPrimaryEmailError(null); }}
                      onKeyDown={(e) => { if (e.key === 'Enter') handleSavePrimaryEmail(); }}
                    />
                    <button
                      onClick={handleSavePrimaryEmail}
                      disabled={primaryEmailSaving}
                      className="px-4 py-2 bg-blue-600 text-white text-sm font-medium rounded-lg hover:bg-blue-700 disabled:opacity-50 transition-colors"
                    >
                      {primaryEmailSaving ? <Loader2 className="w-4 h-4 animate-spin" /> : 'שמירה'}
                    </button>
                    {primaryEmail && (
                      <button
                        onClick={() => { setPrimaryEmailEditing(false); setPrimaryEmailInput(primaryEmail); setPrimaryEmailError(null); }}
                        className="px-3 py-2 text-sm text-gray-500 hover:text-gray-700 transition-colors"
                      >
                        ביטול
                      </button>
                    )}
                  </div>
                  {primaryEmailError && (
                    <p className="text-xs text-red-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      {primaryEmailError}
                    </p>
                  )}
                  {!primaryEmail && (
                    <p className="text-xs text-amber-600 flex items-center gap-1">
                      <AlertCircle className="w-3.5 h-3.5" />
                      לא הוגדרה כתובת מייל — הודעות מייל לא ישלחו עד שתגדיר כתובת
                    </p>
                  )}
                </div>
              )}
            </div>
          </section>

          {/* ── CC emails section ── */}
          <section className="space-y-4">
            <div className="flex items-center gap-2 px-1">
              <AtSign className="w-4 h-4 text-blue-600" />
              <h2 className="text-sm font-bold text-gray-900">כתובות מייל נוספות (CC)</h2>
            </div>

            <p className="text-xs text-gray-500 px-1">
              הוסף עד 2 כתובות מייל נוספות שיקבלו העתק מכל הודעת מייל שנשלחת אליך.
            </p>

            {/* Existing CC emails */}
            {ccEmails.length > 0 && (
              <div className="space-y-2">
                {ccEmails.map(email => (
                  <div
                    key={email}
                    className="flex items-center gap-3 bg-white rounded-xl border border-gray-100 px-4 py-3"
                  >
                    <Mail className="w-4 h-4 text-blue-500 shrink-0" />
                    <span className="flex-1 text-sm text-gray-700 truncate" dir="ltr">{email}</span>
                    <button
                      onClick={() => handleRemoveCcEmail(email)}
                      disabled={ccSaving}
                      className="p-1 rounded-lg text-gray-400 hover:text-red-500 hover:bg-red-50
                                 transition-colors disabled:opacity-40"
                      aria-label="הסר כתובת"
                    >
                      <X className="w-4 h-4" />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Add new CC email */}
            {ccEmails.length < 2 && (
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <div className="relative flex-1">
                    <Mail className="absolute right-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400 pointer-events-none" />
                    <input
                      type="email"
                      value={ccInput}
                      onChange={e => { setCcInput(e.target.value); setCcError(null); }}
                      onKeyDown={e => e.key === 'Enter' && handleAddCcEmail()}
                      placeholder="email@example.com"
                      dir="ltr"
                      disabled={ccSaving}
                      className="w-full pr-10 pl-4 py-3 rounded-xl border border-gray-200
                                 text-sm bg-white placeholder:text-gray-400 text-gray-900
                                 focus:outline-none focus:ring-2 focus:ring-primary-400 focus:border-transparent
                                 disabled:opacity-60 transition"
                    />
                  </div>
                  <button
                    onClick={handleAddCcEmail}
                    disabled={ccSaving || !ccInput.trim()}
                    className="flex items-center justify-center w-11 h-11 rounded-xl
                               bg-primary-600 hover:bg-primary-700 text-white
                               disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                    aria-label="הוסף"
                  >
                    {ccSaving
                      ? <Loader2 className="w-4 h-4 animate-spin" />
                      : <Plus className="w-4 h-4" />}
                  </button>
                </div>
                {ccError && (
                  <p className="text-xs text-red-600 px-1">{ccError}</p>
                )}
              </div>
            )}

            {ccEmails.length >= 2 && (
              <p className="text-xs text-gray-400 px-1">
                הגעת למגבלת 2 כתובות מייל נוספות.
              </p>
            )}
          </section>
        </>
      )}
    </div>
  );
}
